<script setup lang="ts">
import { onMounted, ref, watch } from "vue";
import ListTemplate from "../../../../components/ui/common/ListTemplate.vue";
import LabelLyoutForm from "./LabelLyoutForm.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import NumberInput from "../../../../components/ui/form/NumberInput.vue";
import { useFormValidation } from "../../../../composables/useFormValidation";
import GridInput from "../../../../components/ui/form/GridInput.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";

const props = defineProps<{
  layout?: string;
  readonly?: boolean;
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
}>();

const showForm = ref(false);
const refreshData = ref(false);

const title = "Price Label Layout";

const labelRows = ref<
  {
    name: string;
    caption: string;
    width: number;
    height: number;
    y: number;
    x: number;
    align: string | number;
    font_name: string | number;
    font_size: number;
    font_style: string | number;
    bar_type: string | number;
    default: string | number;
  }[]
>([]);

const selectedRow = ref<{
  name: string;
  caption: string;
  width: number;
  height: number;
  y: number;
  x: number;
  align: string | number;
  font_name: string | number;
  font_size: number;
  font_style: string | number;
  bar_type: string | number;
  default: string | number;
} | null>(null);

const getCaption = (field: string): string => {
  if (field == "barcode") return "Barcode";
  if (field == "code") return "Product Code";
  if (field == "price") return "Price";
  if (field == "item") return "Item Name";
  if (field == "shop") return "Footer Message";
  return "";
};

const prepareLabelRows = () => {
  if (props.layout) {
    const label = props.layout.split("|") || [];
    if (!label || label.length < 2) {
      labelRows.value = [];
      return;
    }
    const lb = label[0].split(":") || [];
    fields.value.width.value = parseFloat(lb[4]) || 0;
    fields.value.height.value = parseFloat(lb[5]) || 0;
    fields.value.top.value = parseFloat(lb[1]) || 0;
    fields.value.left.value = parseFloat(lb[0]) || 0;
    fields.value.right.value = parseFloat(lb[2]) || 0;
    fields.value.orientation.value = lb[3] || "L";

    const l = label[1].split(",") || [];
    labelRows.value = [];
    l.forEach((el: string) => {
      const lr = el.split(":");
      const labelRow = {
        name: lr[0],
        caption: getCaption(lr[0]),
        width: lr.length > 1 ? parseFloat(lr[1]) || 0 : 0,
        height: lr.length > 2 ? parseFloat(lr[2]) || 0 : 0,
        y: lr.length > 3 ? parseFloat(lr[3]) || 0 : 0,
        x: lr.length > 4 ? parseFloat(lr[4]) || 0 : 0,
        align: lr.length > 5 ? lr[5] : "C",
        font_name: lr.length > 6 ? lr[6] : "helvetica",
        font_size: lr.length > 7 ? parseFloat(lr[7]) || 8 : 8,
        font_style: lr.length > 8 ? lr[8] : "",
        bar_type: lr.length > 9 ? lr[9] : "C128",
        default: lr.length > 10 ? lr[10] : "",
      };
      labelRows.value.push(labelRow);
    });
    console.log("labelRows.value", labelRows.value);
  } else {
    labelRows.value = [];
  }
};

const {
  fields,
} = useFormValidation({
  left: 0,
  right: 0,
  top: 0,
  width: 40,
  height: 25,
  orientation: "L",
});

const handleRowClick = (action: string, row: any) => {
  if (action === "edit-field") {
    selectedRow.value = row;
    showForm.value = true;
  }
};

watch(
  () => props.layout,
  () => {
    prepareLabelRows();
  },
  { immediate: true }
);

onMounted(() => {
  //resetForm()
  prepareLabelRows();
});


const saveLayout = () => {
  const layout = `${fields.value.left.value}:${fields.value.top.value}:${fields.value.right.value}:${fields.value.orientation.value}:${fields.value.width.value}:${fields.value.height.value}|`;
  const label = labelRows.value.map((row) => {
    return `${row.name}:${row.width}:${row.height}:${row.y}:${row.x}:${row.align}:${row.font_name}:${row.font_size}:${row.font_style}:${row.bar_type}:${row.default}`;
  });
  const layoutString = layout + label.join(",");
  emit("save", layoutString);
  console.log("layoutString", layoutString);
  // Save the layoutString to the server or local storage
};

const updateLayoutField =(data: any) => {
  const index = labelRows.value.findIndex((row) => row.name === data.name);
  if (index !== -1) {
    labelRows.value[index] = { ...labelRows.value[index], ...data };
  }
  showForm.value = false;
  saveLayout();
};
</script>

<template>
  <ListTemplate
    endPoint=""
    :ignore-permissions="true"
    title="Price Label Layout"
    :ignore-max-height="true"
    :data="labelRows"
    @custom="handleRowClick"
    :show-header="false"
    :fields="[
      {
        name: 'caption',
        caption: 'Field Name',
        sortable: false,
        fieldType: 'text',
      },
      { name: 'width', caption: 'Width', sortable: false, fieldType: 'number' },
      {
        name: 'height',
        caption: 'Height',
        sortable: false,
        fieldType: 'number',
      },
      { name: 'y', caption: 'Pos-Y', sortable: false, fieldType: 'number' },
      { name: 'x', caption: 'Pos-X', sortable: false, fieldType: 'number' },
      { name: 'align', caption: 'Align', sortable: false, fieldType: 'text' },
      {
        name: 'font_name',
        caption: 'Font Name',
        sortable: false,
        fieldType: 'text',
      },
      {
        name: 'font_size',
        caption: 'Font Size',
        sortable: false,
        fieldType: 'number',
      },
      {
        name: 'font_style',
        caption: 'Font Style',
        sortable: false,
        fieldType: 'text',
      },
      {
        name: 'default',
        caption: 'Override Value',
        sortable: false,
        fieldType: 'text',
      },
      {
        name: 'bar_type',
        caption: 'Bar Type',
        sortable: false,
        fieldType: 'text',
      },
    ]"
    :actions="[
      {
        name: 'edit-field',
        icon: 'Edit',
        variant: 'primary',
        title: 'Edit',
        ignorePermission: true,
        visible: () => true,
      },
    ]"
    :filterFields="[]"
    :tabList="[]"
    :refreshData="refreshData"
    @show="showForm = true"
    :showForm="showForm"
    :show-quick-tour="false"
    :show-title="true"
  >
    <template #pre-content>
      <GridInput label="Label Layout" class="mb-6">
        <div class="grid md:grid-cols-3 gap-6 mb-6">
          <NumberInput
            v-model="fields.width.value"
            label="Label Width"
            :readonly="readonly"
            :error="fields.width.touched ? fields.width.error : null"
            @blur="fields.width.touched = true"
          />
          <NumberInput
            v-model="fields.height.value"
            label="Label Height"
            :readonly="readonly"
            :error="fields.height.touched ? fields.height.error : null"
            @blur="fields.height.touched = true"
          />
          <SearchSelect
            v-model="fields.orientation.value"
            label="Orientation"
            :options="[
              { id: 'L', name: 'Landscape' },
              { id: 'P', name: 'Potrait' },
            ]"
            :remote="false"
            :readonly="readonly"
            :error="
              fields.orientation.touched ? fields.orientation.error : null
            "
            @blur="fields.orientation.touched = true"
          />
          <NumberInput
            v-model="fields.top.value"
            label="Top Margin"
            :readonly="readonly"
            :error="fields.top.touched ? fields.top.error : null"
            @blur="fields.top.touched = true"
          />
          <NumberInput
            v-model="fields.left.value"
            label="Left Margin"
            :readonly="readonly"
            :error="fields.left.touched ? fields.left.error : null"
            @blur="fields.left.touched = true"
          />
          <NumberInput
            v-model="fields.right.value"
            label="Right Margin"
            :readonly="readonly"
            :error="fields.right.touched ? fields.right.error : null"
            @blur="fields.right.touched = true"
          />
        </div>
        <div class="grid md:grid-cols-3 gap-6 mb-6">
          <Buttons caption="Save" icon="Save" @click="saveLayout" />

        </div>
      </GridInput>
    </template>
    <template #form-view>
      <LabelLyoutForm
        v-if="showForm"
        :data="selectedRow"
        end-point=""
        :title="title"
        mode="edit"
        @save="updateLayoutField"
        @close="showForm = !showForm"
      />
    </template>
  </ListTemplate>
</template>
